import useDevice from "@/hooks/useDevice";
import useLocalization from "@/hooks/useLocalization";
import useReservation from "@/hooks/useReservation";
import useReservationUI from "@/hooks/useReservationUI";
import useSettings from "@/hooks/useSettings";
import { Box, Stack } from "@mui/material";
import { endOfMonth, startOfMonth } from "date-fns";
import { useMemo } from "react";
import ReservationCalendarDayGrid from "./ReservationCalendarDayGrid";
import ReservationCalendarHeader from "./ReservationCalendarHeader";
import ReservationCalendarWeekdayHeader from "./ReservationCalendarWeekdayHeader";
import {
  filterCalendarReservations,
  getCalendarBounds,
  groupReservationsByDate,
} from "./calendarUtils";
import useCalendarDays from "./useCalendarDays";
import useCalendarFetchRange from "./useCalendarFetchRange";
import useMaxVisibleReservations from "./useMaxVisibleReservations";

type ReservationCalendarProps = {
  selectReservation?: (reservationId: string) => void;
  onDateClick?: (date: string) => void;
  disableReservationSelection?: boolean;
  fullHeight?: boolean;
};

export function ReservationCalendar({
  selectReservation,
  onDateClick,
  disableReservationSelection = false,
  fullHeight = false,
}: ReservationCalendarProps) {
  const { isMobile } = useDevice();
  const { adapterLocale } = useLocalization();
  const { settings } = useSettings();
  const {
    reservations,
    loading,
    fetchReservations,
    fetchedMonthsRange,
    reservationBounds,
  } = useReservation();
  const { calendarMonth, setCalendarMonth, scrollToDate } = useReservationUI();

  const calendarBounds = useMemo(
    () => getCalendarBounds(reservationBounds),
    [reservationBounds],
  );
  const reservationsByDate = useMemo(() => {
    return groupReservationsByDate(filterCalendarReservations(reservations));
  }, [reservations]);

  const displayedMonth = calendarMonth;
  const setDisplayedMonth = (month: Date) => {
    if (reservationBounds.resolved) {
      const monthStart = startOfMonth(month);
      if (
        (calendarBounds.minMonth &&
          monthStart.getTime() < calendarBounds.minMonth.getTime()) ||
        (calendarBounds.maxMonth &&
          monthStart.getTime() > calendarBounds.maxMonth.getTime())
      ) {
        return;
      }
    }
    setCalendarMonth(month);
  };

  const monthStart = startOfMonth(displayedMonth);
  const monthEnd = endOfMonth(displayedMonth);
  const minMonthStart = calendarBounds.minMonth;
  const maxMonthStart = calendarBounds.maxMonth;

  const isPrevDisabled =
    !!minMonthStart && monthStart.getTime() <= minMonthStart.getTime();
  const isNextDisabled =
    !!maxMonthStart && monthStart.getTime() >= maxMonthStart.getTime();

  const { calendarDays, weekdayLabels, weeksInView } = useCalendarDays({
    adapterLocale,
    monthStart,
    monthEnd,
    firstDayOfWeek: settings?.firstDayOfWeek,
  });

  const calendarLoadingRange = useCalendarFetchRange({
    calendarMonth,
    calendarBounds,
    reservationBoundsResolved: reservationBounds.resolved,
    fetchedMonthsRange,
    fetchReservations,
  });

  const { gridRef: daysGridRef, maxVisibleReservations } =
    useMaxVisibleReservations(weeksInView);

  const canSelectReservation =
    !disableReservationSelection && !!selectReservation;

  return (
    <Stack
      spacing={2}
      sx={{
        height: "100%",
        paddingBottom: fullHeight ? 0 : "16px",
        minHeight: 0,
      }}
    >
      <ReservationCalendarHeader
        displayedMonth={displayedMonth}
        isMobile={isMobile}
        minMonthStart={minMonthStart}
        maxMonthStart={maxMonthStart}
        isPrevDisabled={isPrevDisabled}
        isNextDisabled={isNextDisabled}
        onDateClick={(date) => {
          scrollToDate(date);
          onDateClick?.(date);
        }}
        onMonthChange={setDisplayedMonth}
      />
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: "1024px",
          marginX: "auto",
        }}
      >
        <ReservationCalendarWeekdayHeader weekdayLabels={weekdayLabels} />
        <ReservationCalendarDayGrid
          gridRef={daysGridRef}
          calendarDays={calendarDays}
          calendarLoadingRange={calendarLoadingRange}
          loading={loading}
          monthStart={monthStart}
          onDateClick={(date) => {
            scrollToDate(date);
            onDateClick?.(date);
          }}
          reservationsByDate={reservationsByDate}
          maxVisibleReservations={maxVisibleReservations}
          canSelectReservation={canSelectReservation}
          selectReservation={selectReservation}
        />
      </Box>
    </Stack>
  );
}
